import React from 'react';
import styled from '@emotion/styled';
import { motion, useReducedMotion } from 'framer-motion';
import { Utensils, MapPin } from 'lucide-react';

import { useContent } from 'context/ContentContext';
import { useSiteInfo } from 'hooks/useSiteInfo';
import Button from 'components/ui/Button';

const BreakpointSize = 'lg';

// Entrance animation shared by the hero content column and the footer
// callouts. The container staggers each child in; children just fade + rise.
// `baseDelay` lets a later group (e.g. the footer) start its stagger after an
// earlier group is underway, for a cascading reveal. Respects
// prefers-reduced-motion by dropping the stagger/offset/delay entirely.
const getHeroMotionVariants = (reduceMotion, baseDelay = 0) => ({
  container: {
    hidden: {},
    visible: {
      transition: reduceMotion
        ? { staggerChildren: 0, delayChildren: 0 }
        : { staggerChildren: 0.15, delayChildren: baseDelay },
    },
  },
  item: reduceMotion
    ? {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { duration: 0.3 } },
      }
    : {
        hidden: { opacity: 0, y: 24 },
        visible: {
          opacity: 1,
          y: 0,
          transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
        },
      },
});

// Mobile paper panel height (drives the rotated PaperImage sizing below).
const MobilePaperHeight = '70vh';

const HeroContainer = styled.section`
  background-color: ${({ theme }) => theme.colors.tanLight};
  height: 90vh;
  min-height: 780px;
  width: 100%;
  position: relative;
  overflow: hidden;

  ${({ theme }) => theme.mediaQueries.md} {
    min-height: 950px;
  }

  ${({ theme }) => theme.mediaQueries[BreakpointSize]} {
    height: 100svh;
    min-height: 800px;
    max-height: 1024px;
  }
`;

// Both breakpoints layer the paper over the picture via absolute positioning so
// the picture can underlap the paper and show through its torn edge. Mobile:
// paper on top / picture below (torn edge faces down). Desktop: paper left /
// picture right (torn edge faces right).
const HeroWrapper = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
`;

// Paper panel, layered above the picture (higher z-index). Its paper image is
// fully opaque except for a torn edge; the picture underlaps behind that
// transparent edge (see PaperImage / RightPicture) so the tear reads over the
// photo with no cream gap.
const LeftPaper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 55vh;
  min-height: 480px;
  z-index: 2;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: clamp(1rem, 3vw, 2rem);
  /* Top padding clears the fixed header (~7rem) since text sits at the top */
  padding: calc(9rem + 40px) 2rem 3rem;

  ${({ theme }) => theme.mediaQueries.sm} {
    height: 60vh;
    min-height: 600px;
  }

  ${({ theme }) => theme.mediaQueries.md} {
    height: 65vh;
    min-height: 650px;
    padding-top: calc(9rem + 40px);
  }

  ${({ theme }) => theme.mediaQueries.lg} {
    right: auto;
    bottom: 0;
    height: auto;
    width: 70%;
    overflow: visible;
    align-items: center;
    justify-content: center;
    gap: clamp(2rem, 4vw, 4rem);
    padding: 0.5rem 15% 8rem 4rem;
  }

  ${({ theme }) => theme.mediaQueries.xl} {
    padding: 0.5rem 15% 8rem 10rem;
  }
`;

// Paper texture behind the panel content. Mobile: the right-torn asset is
// rotated 90deg so its torn edge faces down; the element is sized to the
// panel's height x width (swapped) so it fills after rotating. Desktop: filled
// flat with the torn edge on the right.
const PaperImage = styled.img`
  position: absolute;
  top: 50%;
  left: 50%;
  /* After the 90deg rotation, width becomes the visual height. Use max() so it
     still covers the panel when its pixel min-height exceeds the vh value on
     short viewports. */
  width: max(${MobilePaperHeight}, 700px);
  height: 100vw;
  transform: translate(-50%, -50%) rotate(90deg);
  object-fit: fill;
  z-index: -1;
  pointer-events: none;

  ${({ theme }) => theme.mediaQueries[BreakpointSize]} {
    inset: 0;
    width: 100%;
    height: 100%;
    transform: none;
    /* Stretch to fill so the torn edge stays at a fixed fraction of the panel
       instead of drifting left as cover-crop zooms on narrower/taller panels.
       Paper grain is uniform, so the stretch is not noticeable. */
    object-fit: fill;
  }
`;

// Food picture, beneath the paper. It spans taller/wider than its visible area
// and underlaps behind the paper's torn edge so the tear shows the photo rather
// than the background. Mobile: bottom, underlaps up. Desktop: right, underlaps
// left.
const RightPicture = styled.img`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 55vh;
  min-height: 450px;
  width: 100%;
  object-fit: cover;
  object-position: center;
  z-index: 1;
  display: block;

  ${({ theme }) => theme.mediaQueries[BreakpointSize]} {
    top: 0;
    left: auto;
    /* Picture (80%) and paper (70%) overlap; the paper's higher z-index keeps
       its opaque body + torn edge over the photo. */
    width: 80%;
    height: 100%;
  }
`;

// Stamp badge in the upper right corner of the hero image (desktop only).
const StampBadge = styled.img`
  display: none;

  ${({ theme }) => theme.mediaQueries[BreakpointSize]} {
    display: block;
    position: absolute;
    top: 10rem;
    right: 5rem;
    width: clamp(20rem, 15vw, 25rem);
    height: auto;
    z-index: 3;
    transform: rotate(45deg);
  }
`;

// Real Ingredients stamp in the upper right of the paper on mobile/tablet.
// Uses a mask so the white PNG can be tinted with the theme green. z-index -1
// keeps it above the paper texture but below the panel content (like
// PaperImage, DOM order puts it over the paper).
const StampBadgeMobile = styled.div`
  position: absolute;
  top: 9rem;
  right: 1rem;
  width: clamp(15rem, 20vw, 14rem);
  aspect-ratio: 1;
  background-color: ${({ theme }) => theme.colors.tertiary};
  mask-image: url('/stamp-real-ingredients.png');
  mask-size: contain;
  mask-repeat: no-repeat;
  mask-position: center;
  opacity: 0.3;
  z-index: -1;
  pointer-events: none;
  transform: rotate(45deg);

  ${({ theme }) => theme.mediaQueries.md} {
    top: 10rem;
    width: 20rem;
  }

  ${({ theme }) => theme.mediaQueries.lg} {
    display: none;
  }
`;

// Tear edge at the bottom of the hero section, overlapping into next section
const TearEdge = styled.img`
  position: absolute;
  bottom: -50px;
  left: 0;
  right: 0;
  width: 100%;
  height: 150px;
  z-index: 4;
  pointer-events: none;

  ${({ theme }) => theme.mediaQueries.md} {
    height: 200px;
  }

  ${({ theme }) => theme.mediaQueries.lg} {
    height: 260px;
  }
`;

// Birreria watermark on the left side of the tear edge
const Watermark = styled.img`
  position: absolute;
  bottom: 20px;
  left: 20px;
  width: clamp(80px, 10vw, 120px);
  height: auto;
  z-index: 5;
  opacity: 0.7;
  pointer-events: none;
  transform: rotate(-15deg);

  ${({ theme }) => theme.mediaQueries.lg} {
    bottom: 30px;
    left: 40px;
    width: clamp(200px, 12vw, 250px);
  }
`;

// Hero footer: three feature callouts (Authentic Taste / Fresh Daily /
// Community Hub) rendered progressively across breakpoints:
//  - Mobile: first two items only, icon + title, no subtitle
//    (HeroFooterSectionMobile).
//  - Tablet (md): all three items with a short subtitle; the third item uses
//    HeroFooterSectionTablet, centered as a group.
//  - Desktop (lg): full layout to the right of the watermark with larger
//    icons, full copy, and dashed dividers between items.
const HeroFooterSections = styled(motion.div)`
  display: flex;
  flex-direction: row;
  gap: 1rem;
  position: absolute;
  bottom: 30px;
  left: 140px;
  right: 20px;
  z-index: 10;
  pointer-events: none;

  ${({ theme }) => theme.mediaQueries.md} {
    left: 50%;
    transform: translateX(-50%);
    right: auto;
    width: max-content;
    gap: 3rem;
    justify-content: center;
  }

  ${({ theme }) => theme.mediaQueries.lg} {
    gap: 0;
    bottom: 60px;
    left: 280px;
    right: 40px;
    transform: none;
    width: auto;
    z-index: 5;
  }
`;

const HeroFooterSection = styled(motion.div)`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  height: 100%;
`;

const HeroFooterSectionWithIcon = styled(HeroFooterSection)`
  flex-direction: row;
  gap: 0.5rem;

  display: none;

  ${({ theme }) => theme.mediaQueries.lg} {
    display: flex;
  }
`;

// Fixed width per desktop feature icon, keyed by $size. Values are tuned per
// icon (pepper/leaf/heart) since their visual weight differs at the same size.
const FOOTER_ICON_SIZES = {
  pepper: 'clamp(3rem, 4vw, 4rem)',
  leaf: 'clamp(3.5rem, 4.5vw, 4.5rem)',
  heart: 'clamp(4rem, 5.5vw, 5.5rem)',
};

// Desktop feature icon (pepper/leaf/heart), sized via FOOTER_ICON_SIZES.
const FooterIcon = styled.img`
  width: ${({ $size }) => FOOTER_ICON_SIZES[$size]};
  height: auto;
  flex-shrink: 0;
`;

// Shared icon for the mobile/tablet footer callouts (same size for all three).
const FooterIconMobile = styled.img`
  width: clamp(1.5rem, 4vw, 2rem);
  height: auto;
  flex-shrink: 0;

  ${({ theme }) => theme.mediaQueries.md} {
    width: clamp(2.2rem, 4vw, 2.5rem);
  }

  ${({ theme }) => theme.mediaQueries.lg} {
    display: none;
  }
`;

const HeroFooterSectionMobile = styled(motion.div)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.5rem;

  ${({ theme }) => theme.mediaQueries.lg} {
    display: none;
  }
`;

const HeroFooterTitleMobile = styled.div`
  font-size: clamp(0.9rem, 2.5vw, 1.1rem);
  font-weight: 600;
  color: ${({ theme, $tone = 'ink' }) => theme.colors[$tone]};
  letter-spacing: 0.05em;
  text-transform: uppercase;

  ${({ theme }) => theme.mediaQueries.md} {
    font-size: clamp(1.1rem, 2.5vw, 1.3rem);
    margin-bottom: 0.25rem;
  }
`;

const HeroFooterSubtitleMobile = styled.div`
  display: none;
  font-size: clamp(0.75rem, 1.8vw, 0.9rem);
  font-weight: 400;
  color: ${({ theme }) => theme.colors.ink};
  letter-spacing: 0.03em;

  ${({ theme }) => theme.mediaQueries.md} {
    display: block;
  }
`;

// Third footer callout ("Community Hub"), shown only at the tablet
// breakpoint — hidden on mobile (2-item row) and superseded by the full
// desktop layout below.
const HeroFooterSectionTablet = styled(HeroFooterSectionMobile)`
  display: none;

  ${({ theme }) => theme.mediaQueries.md} {
    display: flex;
  }

  ${({ theme }) => theme.mediaQueries.lg} {
    display: none;
  }
`;

const HeroFooterTitle = styled.div`
  font-size: clamp(1.2rem, 1.5vw, 1.6rem);
  font-weight: 700;
  color: ${({ theme, $tone = 'ink' }) => theme.colors[$tone]};
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin-bottom: 0.25rem;
  text-align: center;
`;

const HeroFooterSubtitle = styled.div`
  font-size: clamp(0.9rem, 1.2vw, 1.1rem);
  font-weight: 400;
  color: ${({ theme }) => theme.colors.ink};
  letter-spacing: 0.05em;
  text-align: center;
  text-wrap: balance;
`;

const HeroFooterDivider = styled.div`
  width: 1px;
  border-left: 1px dashed ${({ theme }) => theme.colors.primary};

  display: none;

  ${({ theme }) => theme.mediaQueries.lg} {
    display: block;
  }
`;

// Left-aligned content column, centered as a block within the paper (see
// LeftPaper align-items: center). max-width keeps it a tidy column. Also the
// stagger container for the entrance animation (see getHeroMotionVariants).
const Content = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: clamp(1.5rem, 3vw, 2.5rem);
  width: 100%;
  max-width: 100rem;

  ${({ theme }) => theme.mediaQueries.lg} {
    align-items: flex-start;
    text-align: left;
  }
`;

const TextSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: clamp(1rem, 2vw, 2rem);
  text-wrap: balance;
  max-width: 600px;
  margin-bottom: 1.5rem;

  ${({ theme }) => theme.mediaQueries.lg} {
    align-items: flex-start;
    text-align: left;
  }
`;

// Eyebrow / kicker above the headline (e.g. "HECHO CON PASIÓN").
const Eyebrow = styled(motion.div)`
  display: none;

  ${({ theme }) => theme.mediaQueries.lg} {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 0.6rem;
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 700;
    letter-spacing: 0.2em;
    text-transform: uppercase;

    img {
      width: 16px;
      height: 16px;
      flex: 0 0 auto;
    }
    font-size: clamp(1.85rem, 1.1vw, 2rem);
  }
`;

const Title = styled(motion.h1)`
  ${({ theme }) => theme.mixins.textH1};
  color: ${({ theme }) => theme.colors.ink};
  /* Larger and fluid: scales with the viewport between the two bounds. */
  font-size: clamp(5.5rem, 8.5vw, 9rem);
  line-height: 1.02;
  text-align: center;
  max-width: 100%;
  margin-bottom: 1rem;

  &::after {
    display: none;
  }

  ${({ theme }) => theme.mediaQueries.lg} {
    text-align: left;
    &::after {
      content: '';
      width: clamp(12rem, 22vw, 18rem);
      aspect-ratio: 624 / 137;
      margin: 0.75rem 0 0;
      background-image: url('https://storage.googleapis.com/birreria-el-semaforo/images/accent-green-slashes/l.webp');
      background-size: contain;
      background-repeat: no-repeat;
      background-position: left center;
      margin: 0.75rem 0 0;
    }
  }
`;

// Colored segment of the headline ($tone maps to a theme color key).
const TitlePart = styled.span`
  color: ${({ theme, $tone }) => theme.colors[$tone]};
`;

const Subtitle = styled(motion.p)`
  ${({ theme }) => theme.mixins.textP1};
  color: ${({ theme }) => theme.colors.ink};
  /* Larger and fluid to match the headline. */
  font-size: clamp(1.6rem, 1.7vw, 2rem);
`;

// Color the headline in two segments split on the first comma: the first
// clause (incl. comma) in red, the remainder in green — e.g. "Mi Casa," red /
// "Tu Craving" green.
const renderColoredTitle = (text) => {
  const trimmed = (text || '').trim();
  if (!trimmed) return null;

  const commaIdx = trimmed.indexOf(',');
  if (commaIdx === -1) {
    return <TitlePart $tone="primary">{trimmed}</TitlePart>;
  }

  const head = trimmed.slice(0, commaIdx + 1);
  const tail = trimmed.slice(commaIdx + 1).trim();

  return (
    <>
      <TitlePart $tone="primary">{head}</TitlePart>
      {tail ? (
        <>
          <br />
          <TitlePart $tone="tertiary">{tail}</TitlePart>
        </>
      ) : null}
    </>
  );
};

const ButtonContainer = styled(motion.div)`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 0.75rem;
  width: 100%;
  max-width: 40rem;

  button {
    flex: 1;
    min-width: 0;
    white-space: nowrap;
  }

  ${({ theme }) => theme.mediaQueries.md} {
    flex-wrap: nowrap;
    gap: 1rem;
    padding-top: 1rem;
  }
`;

// Focus-visible ring for keyboard navigation. Defined as a function (not a
// plain string) so it receives the styled-components theme when interpolated
// below. Uses the hex + alpha-suffix convention from Button.jsx (e.g. `${bg}40`)
// for a translucent theme color instead of a hard-coded rgba() value.
const FocusStyles = ({ theme }) => `
  &:focus-visible {
    outline: 2px solid ${theme.colors.primary};
    outline-offset: 2px;
    box-shadow: 0 0 0 4px ${theme.colors.primary}4D;
  }
`;

// Apply focus styles to the Button component
const AccessibleButton = styled(Button)`
  ${FocusStyles}
`;

const PrimaryButtonWrapper = styled.div`
  flex: 1;
  min-width: 0;

  button {
    width: 100%;
  }
`;

const Hero = () => {
  const { hero, messages = {} } = useContent();
  const { isClosed, rawShoppingEnabled } = useSiteInfo();
  const {
    title,
    imageAlt,
    subtitle,
    primaryButtonText,
    secondaryButtonText,
    eyebrow,
  } = hero;

  // Right-side picture (hardcoded placeholder; swaps to content-driven later)
  const pictureAlt = imageAlt || 'Birria quesadilla platter';

  // Entrance animation variants for the content column and footer callouts;
  // the footer starts after a brief pause and animates all items together
  // (no stagger) for a snappier reveal. Falls back to a simple fade when the
  // user prefers reduced motion.
  const shouldReduceMotion = useReducedMotion();
  const { container: contentVariants, item: itemVariants } =
    getHeroMotionVariants(shouldReduceMotion);
  const { container: footerVariantsBase, item: footerItemVariants } =
    getHeroMotionVariants(shouldReduceMotion, 0.15);

  // Override the footer container to remove stagger so all items animate together
  const footerVariants = shouldReduceMotion
    ? footerVariantsBase
    : {
        ...footerVariantsBase,
        visible: {
          ...footerVariantsBase.visible,
          transition: { staggerChildren: 0, delayChildren: 0.15 },
        },
      };

  // Determine button text: show "Menu" when shopping disabled OR store closed
  const shouldShowMenu = !rawShoppingEnabled || isClosed;
  const menuButtonText = messages.menuButtonFallback || 'Menu';

  // Scroll handler functions
  const handleScrollToMenu = () => {
    const menuSection = document.getElementById('menu');
    if (menuSection) {
      menuSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleScrollToLocation = () => {
    const locationSection = document.getElementById('location');
    if (locationSection) {
      locationSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <HeroContainer role="banner" id="hero" aria-labelledby="hero-title">
      <HeroWrapper>
        <LeftPaper>
          <PaperImage
            src="https://storage.googleapis.com/birreria-el-semaforo/images/hero-background-torn/xl.webp"
            alt=""
            aria-hidden="true"
            fetchpriority="high"
            decoding="async"
            loading="eager"
          />
          <StampBadgeMobile aria-hidden="true" />
          <Content
            variants={contentVariants}
            initial="hidden"
            animate="visible"
          >
            <TextSection>
              <Eyebrow variants={itemVariants}>
                <img
                  src="https://storage.googleapis.com/birreria-el-semaforo/images/accent-green-star/s.webp"
                  alt=""
                  aria-hidden="true"
                />
                {eyebrow || 'Hecho con pasión'}
                <img
                  src="https://storage.googleapis.com/birreria-el-semaforo/images/accent-green-star/s.webp"
                  alt=""
                  aria-hidden="true"
                />
              </Eyebrow>
              <Title id="hero-title" variants={itemVariants}>
                {renderColoredTitle(title)}
              </Title>
              <Subtitle id="hero-subtitle" variants={itemVariants}>
                {subtitle}
              </Subtitle>
            </TextSection>
            <ButtonContainer variants={itemVariants}>
              <PrimaryButtonWrapper>
                <AccessibleButton
                  variant="primary"
                  size="large"
                  onClick={handleScrollToMenu}
                  aria-labelledby="primary-btn-text"
                  title={shouldShowMenu ? menuButtonText : primaryButtonText}
                >
                  <Utensils size={20} aria-hidden="true" />
                  <span id="primary-btn-text">
                    {shouldShowMenu ? menuButtonText : primaryButtonText}
                  </span>
                </AccessibleButton>
              </PrimaryButtonWrapper>
              <AccessibleButton
                defaultColor="tertiary"
                hoverColor="tertiaryDark"
                variant="secondary"
                size="large"
                onClick={handleScrollToLocation}
                aria-labelledby="secondary-btn-text"
              >
                <MapPin size={20} aria-hidden="true" />
                <span id="secondary-btn-text">{secondaryButtonText}</span>
              </AccessibleButton>
            </ButtonContainer>
          </Content>
        </LeftPaper>
        <RightPicture
          src="https://storage.googleapis.com/birreria-el-semaforo/images/hero-background/xl.webp"
          alt={pictureAlt}
          fetchpriority="high"
          decoding="async"
          loading="eager"
          width="1536"
          height="1024"
        />
        <StampBadge
          src="https://storage.googleapis.com/birreria-el-semaforo/images/stamp-real-ingredients/m.webp"
          alt=""
          aria-hidden="true"
        />
      </HeroWrapper>
      <TearEdge
        src="https://storage.googleapis.com/birreria-el-semaforo/images/tear-edge-yellow/l.webp"
        alt=""
        aria-hidden="true"
      />
      <Watermark
        src="https://storage.googleapis.com/birreria-el-semaforo/images/stamp-birreria-watermark-green/m.webp"
        alt=""
        aria-hidden="true"
      />
      <HeroFooterSections
        variants={footerVariants}
        initial="hidden"
        animate="visible"
      >
        <HeroFooterSectionMobile variants={footerItemVariants}>
          <FooterIconMobile
            src="https://storage.googleapis.com/birreria-el-semaforo/images/icon-pepper/s.webp"
            alt=""
            aria-hidden="true"
          />
          <div>
            <HeroFooterTitleMobile $tone="primary">
              Authentic Taste
            </HeroFooterTitleMobile>
            <HeroFooterSubtitleMobile>
              Traditional recipes
            </HeroFooterSubtitleMobile>
          </div>
        </HeroFooterSectionMobile>
        <HeroFooterSectionMobile variants={footerItemVariants}>
          <FooterIconMobile
            src="https://storage.googleapis.com/birreria-el-semaforo/images/icon-leaf/s.webp"
            alt=""
            aria-hidden="true"
          />
          <div>
            <HeroFooterTitleMobile $tone="tertiary">
              Fresh Daily
            </HeroFooterTitleMobile>
            <HeroFooterSubtitleMobile>
              Quality ingredients
            </HeroFooterSubtitleMobile>
          </div>
        </HeroFooterSectionMobile>
        <HeroFooterSectionTablet variants={footerItemVariants}>
          <FooterIconMobile
            src="https://storage.googleapis.com/birreria-el-semaforo/images/icon-heart/s.webp"
            alt=""
            aria-hidden="true"
          />
          <div>
            <HeroFooterTitleMobile $tone="primary">
              Community
            </HeroFooterTitleMobile>
            <HeroFooterSubtitleMobile>
              Your neighborhood spot
            </HeroFooterSubtitleMobile>
          </div>
        </HeroFooterSectionTablet>
        <HeroFooterSectionWithIcon variants={footerItemVariants}>
          <FooterIcon
            $size="pepper"
            src="https://storage.googleapis.com/birreria-el-semaforo/images/icon-pepper/s.webp"
            alt=""
            aria-hidden="true"
          />
          <div>
            <HeroFooterTitle $tone="primary">Authentic Taste</HeroFooterTitle>
            <HeroFooterSubtitle>
              Traditional recipes with modern flair
            </HeroFooterSubtitle>
          </div>
        </HeroFooterSectionWithIcon>
        <HeroFooterDivider />
        <HeroFooterSectionWithIcon variants={footerItemVariants}>
          <FooterIcon
            $size="leaf"
            src="https://storage.googleapis.com/birreria-el-semaforo/images/icon-leaf/s.webp"
            alt=""
            aria-hidden="true"
          />
          <div>
            <HeroFooterTitle $tone="tertiary">Fresh Made Daily</HeroFooterTitle>
            <HeroFooterSubtitle>
              Quality ingredients prepared daily
            </HeroFooterSubtitle>
          </div>
        </HeroFooterSectionWithIcon>
        <HeroFooterDivider />
        <HeroFooterSectionWithIcon variants={footerItemVariants}>
          <FooterIcon
            $size="heart"
            src="https://storage.googleapis.com/birreria-el-semaforo/images/icon-heart/s.webp"
            alt=""
            aria-hidden="true"
          />
          <div>
            <HeroFooterTitle $tone="primary">Community</HeroFooterTitle>
            <HeroFooterSubtitle>
              Your neighborhood dining destination
            </HeroFooterSubtitle>
          </div>
        </HeroFooterSectionWithIcon>
      </HeroFooterSections>
    </HeroContainer>
  );
};

export default Hero;
